// web/src/components/browser/PhotoTile.tsx
import { cn } from "../../lib/utils";
import { Thumbnail } from "../primitives/Thumbnail";
import { useNavigation } from "../../stores/navigation";
import { useLongPress } from "../../hooks/useLongPress";

interface Props {
  folderPath: string;
  fileName: string;
  mime?: string;
  videoDurationLabel?: string;
  isRaw?: boolean;
  onTap: () => void;
}

export function PhotoTile({
  folderPath,
  fileName,
  mime,
  videoDurationLabel,
  isRaw,
  onTap,
}: Props) {
  const selected = useNavigation((s) => s.selection.has(fileName));
  const active = useNavigation((s) => s.selectionActive);
  const toggle = useNavigation((s) => s.toggleSelection);
  const activate = useNavigation((s) => s.activateSelection);
  const { didLongPress, ...handlers } = useLongPress(() => activate(fileName));

  return (
    <button
      {...handlers}
      onClick={(e) => {
        if (didLongPress()) return;
        if (active) {
          toggle(fileName);
          return;
        }
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          toggle(fileName);
          return;
        }
        onTap();
      }}
      className={cn(
        "relative aspect-square overflow-hidden rounded-md border border-white/5 bg-white/5",
        selected && "outline outline-2 outline-accent",
      )}
    >
      <Thumbnail folderPath={folderPath} fileName={fileName} mime={mime} />
      {selected && (
        <span className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white">
          ✓
        </span>
      )}
      {videoDurationLabel && (
        <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1.5 py-0.5 font-mono text-[9px] text-white">
          {videoDurationLabel}
        </span>
      )}
      {mime?.startsWith("video/") && (
        <span
          aria-label="Video"
          className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55"
        >
          <svg
            viewBox="0 0 12 12"
            className="h-3 w-3 fill-white"
            aria-hidden="true"
          >
            <path d="M3.5 2 L10 6 L3.5 10 Z" />
          </svg>
        </span>
      )}
      {isRaw && (
        <span className="absolute right-1 top-1 rounded bg-black/55 px-1.5 py-0.5 font-mono text-[9px] text-white">
          RAW
        </span>
      )}
    </button>
  );
}
